<template>
  <div class="search-agent">
    <div class="search-agent__head">
      <h2 class="search-agent__title">Opret en søgeagent</h2>
      <p class="search-agent__text">
        Modtag automatisk en email når der er nye annoncer, der matcher denne
        søgning.
      </p>
    </div>
    <div
      class="search-agent__content search-agent__content--wide"
      v-if="maxResultCountForCreationExceeded"
    >
      <p class="search-agent__high-ad-count">Søgningen er for omfattende.</p>
      <p class="search-agent__high-ad-count">
        Afgræns den yderligere gennem filtrér, så der maximalt gives 1000
        resultater.
      </p>
    </div>
    <div
      class="search-agent__content"
      v-if="!maxResultCountForCreationExceeded"
    >
      <div class="search-agent__content-input">
        <div class="search-agent__input-container">
          <p class="error my-1" v-if="searchAgentNameErrorMessage">
            <span>{{ searchAgentNameErrorMessage }}</span>
            <span class="error__attention"></span>
          </p>
          <div class="input-container">
            <input
              ref="searchAgentName"
              title="SearchAgentName"
              class="search-agent__input form-control"
              :class="{
                'error--input': searchAgentNameErrorMessage,
              }"
              type="text"
              placeholder="Navngivning, ex: 'Rød Mazda under 100.000'"
              autocomplete="off"
              v-model="searchAgentName"
              @keyup.enter="createSearchAgent()"
            />
            <label class="control-label" for="searchAgentName">
              Navn på din søgeagent
            </label>
          </div>
        </div>
        <div class="flex md:hidden items-center justify-between">
          <p class="font-bold text-base">Notifikationer</p>
          <div
            class="relative bg-gray-300 py-1 px-4 rounded-full flex items-center gap-6"
          >
            <button
              style="outline: none"
              class="z-10 text-white text-sm select-none"
              @click="notificationIntervalDaily = false"
            >
              Straks
            </button>
            <button
              style="outline: none"
              class="z-10 text-white text-sm select-none"
              @click="notificationIntervalDaily = true"
            >
              Dagligt
            </button>
            <span
              class="z-0 absolute bg-green-500 h-7 top-0 left-0 rounded-full transition"
              :style="
                notificationIntervalDaily
                  ? 'width: 84px; transform: translateX(67px);'
                  : 'width: 76px; transform: translateX(0);'
              "
            />
          </div>
        </div>
        <div class="search-agent__button-container">
          <button
            type="button"
            class="button button--highlight search-agent__button"
            @click.prevent="createSearchAgent()"
            :disabled="inProgress"
          >
            opret søgeagent
            <ButtonSpinner v-if="inProgress" />
          </button>
        </div>
      </div>
      <div class="hidden md:flex items-center gap-4">
        <p class="font-bold text-base">Notifikationer</p>
        <div
          class="relative bg-gray-300 py-1 px-4 rounded-full flex items-center gap-6"
        >
          <button
            style="outline: none"
            class="z-10 text-white text-sm select-none"
            @click="notificationIntervalDaily = false"
          >
            Straks
          </button>
          <button
            style="outline: none"
            class="z-10 text-white text-sm select-none"
            @click="notificationIntervalDaily = true"
          >
            Dagligt
          </button>
          <span
            class="z-0 absolute bg-green-500 h-7 top-0 left-0 rounded-full transition"
            :style="
              notificationIntervalDaily
                ? 'width: 84px; transform: translateX(67px);'
                : 'width: 76px; transform: translateX(0);'
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { app } from '@/main'

import ButtonSpinner from '@components/Shared/ButtonSpinner/ButtonSpinner.vue'

export default {
  components: { ButtonSpinner },
  props: {
    eventBus: { default: null, type: Object },
    searchRequestId: { default: null, type: Number },
    maxResultCountForCreationExceeded: { default: false, type: Boolean },
    suggestedName: { default: '', type: String },
  },
  data() {
    return {
      searchAgentName: '',
      searchAgentNameErrorMessage: '',
      inProgress: false,
      notificationIntervalDaily: false,
    }
  },
  watch: {
    searchAgentName() {
      this.searchAgentNameErrorMessage = ''
    },
  },
  methods: {
    createSearchAgent() {
      this.inProgress = true
      if (!this.searchAgentName) {
        this.searchAgentNameErrorMessage = 'Navngiv venligst din søgeagent'
        this.inProgress = false
        return
      }
      const data = {
        SearchRequestId: this.searchRequestId,
        SearchAgentName: this.searchAgentName,
        NotificationIntervalDaily: this.notificationIntervalDaily,
      }

      app.axios
        .post('/api/searchagent/create', JSON.stringify(data))
        .then(resp => {
          if (
            resp.data.errorCollection &&
            resp.data.errorCollection.length > 0
          ) {
            resp.data.errorCollection.forEach(value => {
              switch (value.errorItem) {
                case 'SearchAgentName':
                  this.searchAgentNameErrorMessage = value.errorMessage
                  break
                case 'SearchCountLimit':
                  this.searchAgentNameErrorMessage = value.errorMessage
                  break
                case 'SearchRequestId':
                  app.toast.error(value.errorMessage)
                  break
                case 'authentication':
                  app.toast.info(
                    'Du skal være logget ind for at oprette en søgeagent. Det er gratis at oprette en profil.',
                    () => {
                      app.mitt.emit('prompt-login', undefined)
                    },
                    'Gå til login',
                    'Luk'
                  )
                  break
                default:
                  app.toast.error(value.errorMessage)
                  break
              }
            })
            this.inProgress = false
            return
          }
          const searchAgentNameTemp = this.searchAgentName
          this.searchAgentName = this.suggestedName

          const owner = this
          setTimeout(() => {
            owner.eventBus.emit('search-agent-overlay-close')
          }, 150)
          app.toast.success(
            `Din søgeagent "${searchAgentNameTemp}" er nu oprettet`
          )
          this.inProgress = false
        })
    },
    focusSearchAgentName() {
      this.searchAgentName = this.suggestedName
      this.$refs.searchAgentName.focus()
    },
    reset() {
      this.searchAgentNameErrorMessage = ''
      this.inProgress = false
    },
  },
  created() {
    this.searchAgentName = this.suggestedName
    this.eventBus.on('search-agent-focus-name', this.focusSearchAgentName)
  },
  beforeUnmount() {
    this.eventBus.off('search-agent-focus-name', this.focusSearchAgentName)
  },
}
</script>

<style lang="scss">
@import 'SearchAgent.scss';
</style>
